import React, { useState, useEffect, memo, useContext } from 'react';
import DatePicker from '../../components/dateComponents/DatePicker2';
import CustomChart from '../../components/chartComponents/customChart';
import { dbPost } from '../../apis/commonApi';
import { formatDateToISOString } from '../../services/UtilService';
import { ModalContext } from '../../components/contexts';

const SelectBox = ({ id, options, defaultText, onChange }) => (
  <select
    id={id}
    className="form-select md"
    onChange={e => onChange(e.target.value)}
  >
    <option value="">{defaultText}</option>
    {options.map((option, index) => (
      <option key={index} value={option}>
        {option}
      </option>
    ))}
  </select>
);

const DateFilter = memo(
  ({
    selectedPeriod,
    onPeriodSelect,
    customStartDate,
    customEndDate,
    handleStartDateChange,
    handleEndDateChange,
  }) => {
    const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
    const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
    const { modalService } = useContext(ModalContext);
    const [displayStartDate, setDisplayStartDate] = useState('');
    const [displayEndDate, setDisplayEndDate] = useState('');

    const formatDateForDisplay = date => {
      if (!date) return '';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const validateDates = (date, comparisonDate, type) => {
      if (type === 'endDate' && !comparisonDate) {
        modalService.errorAlert('먼저 시작 날짜를 선택하세요.');
        return false;
      }

      const selectedDate = new Date(date);
      const compareDate = comparisonDate ? new Date(comparisonDate) : null;

      if (type === 'startDate' && compareDate && selectedDate > compareDate) {
        modalService.errorAlert(
          '시작 날짜는 종료 날짜보다 이후일 수 없습니다.'
        );
        return false;
      }

      if (type === 'endDate' && compareDate && selectedDate < compareDate) {
        modalService.errorAlert(
          '종료 날짜는 시작 날짜보다 이전일 수 없습니다.'
        );
        return false;
      }

      return true;
    };

    useEffect(() => {
      setDisplayStartDate(
        customStartDate ? formatDateForDisplay(customStartDate) : ''
      );
      setDisplayEndDate(
        customEndDate ? formatDateForDisplay(customEndDate) : ''
      );
    }, [customStartDate, customEndDate]);

    const handleStartDateInternalChange = date => {
      const formattedDate = formatDateToISOString(date);
      if (!validateDates(formattedDate, customEndDate, 'startDate')) {
        return;
      }
      handleStartDateChange(formattedDate);
      setDisplayStartDate(formatDateForDisplay(date));
    };

    const handleEndDateInternalChange = date => {
      const formattedDate = formatDateToISOString(date);
      if (!validateDates(formattedDate, customStartDate, 'endDate')) {
        return;
      }
      handleEndDateChange(formattedDate);
      setDisplayEndDate(formatDateForDisplay(date));
    };

    return (
      <td>
        <div className="input-group">
          {['전체', '3개월', '6개월', '1년', '2년', '3년', '기간 조회'].map(
            (label, idx) => (
              <div className="form-chip sm" key={idx}>
                <input
                  type="radio"
                  className="radio"
                  name="rdo_chip_period"
                  id={`rdo_chip_period_${idx}`}
                  checked={selectedPeriod === label}
                  onChange={() => onPeriodSelect(label)}
                />
                <label
                  className="form-chip-outline"
                  htmlFor={`rdo_chip_period_${idx}`}
                  style={{
                    height: 'auto',
                  }}
                >
                  {label}
                </label>
              </div>
            )
          )}
        </div>
        {selectedPeriod === '기간 조회' && (
          <div className="input-group gap-md">
            <DatePicker
              selectedDate={displayStartDate}
              onDateChange={handleStartDateInternalChange}
              label="시작 날짜"
            />
            <DatePicker
              selectedDate={displayEndDate}
              onDateChange={handleEndDateInternalChange}
              label="종료 날짜"
            />
          </div>
        )}
      </td>
    );
  }
);

const ChartSection = memo(({ title, chartData }) => {
  const transformedData = {
    categories: chartData.map(item => item.taskNm),
    series: [
      {
        name: '개선항목',
        data: chartData.map(item => item.impvCnt),
      },
      {
        name: '개선완료',
        data: chartData.map(item => item.impvYCnt),
      },
    ],
  };

  const options = {
    chart: {
      width: '100%',
      height: '400px',
      usageStatistics: false,
    },
    xAxis: {
      title: '공정별',
      pointOnColumn: true,
      label: {
        formatter: value =>
          value.length > 5 ? value.substring(0, 5) + '...' : value,
      },
    },
    yAxis: {
      title: '항목 개수',
    },
    series: {
      vertical: true,
    },
    tooltip: {
      formatter: value => `${value}개`,
    },
  };

  return (
    <div>
      <strong className="content-tit">{title}</strong>
      <div className="txt-box bg-white gap-md">
        <CustomChart type="bar" data={transformedData} options={options} />
      </div>
    </div>
  );
});

const ImpvEnv = () => {
  useEffect(() => {
    document.title = '통계 - 작업환경 개선사항 ';
  }, []);
  const [selectedPeriod, setSelectedPeriod] = useState('전체');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCustomPeriod, setIsCustomPeriod] = useState(false);
  const [taskName, setTaskName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [taskNameOptions, setTaskNameOptions] = useState([]);
  const [businessTypeOptions, setBusinessTypeOptions] = useState([]);
  const [initialTaskNameOptions, setInitialTaskNameOptions] = useState([]);
  const [initialBusinessTypeOptions, setInitialBusinessTypeOptions] = useState(
    []
  );

  const fetchData = async (params, isInitialLoad = false) => {
    setIsLoading(true);
    try {
      const response = await dbPost('/stats/impvEnv/search.do', params || {});
      const uniqueTaskNames = [...new Set(response.map(item => item.taskNm))];
      const uniqueBusinessTypes = [
        ...new Set(response.map(item => item.tpBizNm)),
      ];

      if (isInitialLoad) {
        setInitialTaskNameOptions(uniqueTaskNames);
        setInitialBusinessTypeOptions(uniqueBusinessTypes);
      }

      setTaskNameOptions(uniqueTaskNames);
      setBusinessTypeOptions(uniqueBusinessTypes);
      setChartData(response || []);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const onDateChange = ({ startDate, endDate }) => {
    fetchData({
      startDate,
      endDate,
      taskNm: taskName,
      tpBizNm: businessType,
    });
  };

  useEffect(() => {
    fetchData(null, true);
  }, []);

  const formatDateForDisplay = date => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const validateDates = (date, comparisonDate, type) => {
    if (type === 'endDate' && !comparisonDate) {
      modalService.errorAlert('먼저 시작 날짜를 선택하세요.');
      return false;
    }

    const selectedDate = new Date(date);
    const compareDate = comparisonDate ? new Date(comparisonDate) : null;

    if (type === 'startDate' && compareDate && selectedDate > compareDate) {
      modalService.errorAlert('시작 날짜는 종료 날짜보다 이후일 수 없습니다.');
      return false;
    }

    if (type === 'endDate' && compareDate && selectedDate < compareDate) {
      modalService.errorAlert('종료 날짜는 시작 날짜보다 이전일 수 없습니다.');
      return false;
    }

    return true;
  };

  const handleStartDateChange = date => {
    const formattedDate = formatDateToISOString(date);
    if (!validateDates(formattedDate, customEndDate, 'startDate')) {
      return;
    }
    setCustomStartDate(formattedDate);
    onDateChange({ startDate: formattedDate, endDate: customEndDate });
  };

  const handleEndDateChange = date => {
    const formattedDate = formatDateToISOString(date);
    if (!validateDates(formattedDate, customStartDate, 'endDate')) {
      return;
    }
    setCustomEndDate(formattedDate);
    onDateChange({ startDate: customStartDate, endDate: formattedDate });
  };

  const handlePeriodSelect = label => {
    setSelectedPeriod(label);
    setIsCustomPeriod(label === '기간 조회');

    if (label !== '기간 조회') {
      const today = new Date();
      let startDate = null;
      let endDate = new Date();

      if (label === '3개월') {
        startDate = new Date(today.setMonth(today.getMonth() - 3));
      } else if (label === '6개월') {
        startDate = new Date(today.setMonth(today.getMonth() - 6));
      } else if (label === '1년') {
        startDate = new Date(today.setFullYear(today.getFullYear() - 1));
      } else if (label === '2년') {
        startDate = new Date(today.setFullYear(today.getFullYear() - 2));
      } else if (label === '3년') {
        startDate = new Date(today.setFullYear(today.getFullYear() - 3));
      }

      const formattedStartDate = formatDateToISOString(startDate);
      const formattedEndDate = formatDateToISOString(endDate, true);

      fetchData({ startDate: formattedStartDate, endDate: formattedEndDate });
    }
  };

  const handleSearch = () => {
    const startDate = formatDateToISOString(customStartDate);
    const endDate = formatDateToISOString(customEndDate, true);

    fetchData({ startDate, endDate, taskNm: taskName, tpBizNm: businessType });
  };

  return (
    <div className="contents">
      <div className="page-title-wrap" data-type="responsive">
        <h2 className="h-tit">작업 환경 개선 사항 조회</h2>
      </div>
      <div className="tbl-wrap">
        <table className="tbl data row">
          <caption>조회 조건 선택 테이블입니다.</caption>
          <tbody>
            <tr>
              <th scope="row">조회 기간</th>
              <DateFilter
                selectedPeriod={selectedPeriod}
                onPeriodSelect={handlePeriodSelect}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                handleStartDateChange={handleStartDateChange}
                handleEndDateChange={handleEndDateChange}
              />
            </tr>
            <tr>
              <th scope="row">조회 구분</th>
              <td>
                <div className="input-group">
                  <label className="hidden-label" htmlFor="businessType">
                    업종
                  </label>
                  <SelectBox
                    id="businessType"
                    options={Array.from(
                      new Set([
                        ...initialBusinessTypeOptions,
                        ...businessTypeOptions,
                      ])
                    )}
                    defaultText="업종 선택"
                    onChange={setBusinessType}
                    aria-labelledby="businessType"
                  />
                  <label className="hidden-label" htmlFor="taskName">
                    공정
                  </label>
                  <SelectBox
                    id="taskName"
                    options={Array.from(
                      new Set([...initialTaskNameOptions, ...taskNameOptions])
                    )}
                    defaultText="공정 선택"
                    onChange={setTaskName}
                    aria-labelledby="task"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="page-btn-wrap gap-lg">
        <button
          type="button"
          className="btn xlg primary"
          onClick={handleSearch}
        >
          검색하기
        </button>
      </div>
      <div className="tbl-wrap">
        {isLoading ? (
          <p>데이터를 불러오는 중입니다...</p>
        ) : (
          <ChartSection
            title="작업 환경 개선 사항 조회"
            chartData={chartData}
          />
        )}
      </div>
    </div>
  );
};

export default ImpvEnv;
